import _ from 'lodash';
import { FormatUtility } from '@codequality/vue-cli-base';

import { Eフェーズ種別 } from '@/openapi/enum';
import { Cデータ役員借入, データListItemResponse } from '@/openapi/user';

import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import AppFormatUtility from '@/classes/common/AppFormatUtility';

export type 役員借入ListItem = Cデータ役員借入 & {
  データpk?: number;
  番号?: number;
  フェーズ種別?: Eフェーズ種別;
};

type 残高 = Required<Pick<Cデータ役員借入, '前期末残高' | '元本返済額月' | '直近月残高'>>;

export type 役員借入 = {
  list: 役員借入ListItem[];
  total: 残高;
};

export default class 役員借入Service extends AbstractデータService<役員借入> {
  protected serviceType = ServiceType.役員借入;

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse(データs: データListItemResponse[]): 役員借入 {
    // 必要データに絞り込んで計算可能データ化
    const filteredデータs = this.入力分類Filter(データs);

    // 一覧
    const list = filteredデータs.map((d) => {
      return {
        データpk: d.pk,
        番号: d.c番号,
        フェーズ種別: d.cフェーズ種別,
        ...(JSON.parse(d.cデータ) as Cデータ役員借入),
      };
    });

    // 合計
    const total: 残高 = {
      前期末残高: 0,
      元本返済額月: 0,
      直近月残高: 0,
    };
    list.forEach((listItem) => {
      for (const key of Object.keys(total)) {
        total[key as keyof 残高] += Number(listItem[key as keyof 役員借入ListItem] ?? 0);
      }
    });

    return {
      list,
      total,
    };
  }

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse帳票ListItemRequest(データs: データListItemResponse[]): 帳票データ {
    const raw = this.parse(データs);

    // テンプレート行設定
    const templateConfig = {
      list: {
        header: 0,
        row: {
          odd: 1,
          even: 2,
        },
        total: 3,
      },
    };

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfig.list.header];

    const values: unknown[][] = [
      [
        ...this.templateCellValues(7),
        `直近月残高\n(${AppFormatUtility.formatYm(AbstractデータService.計画書.c実績直近月)})`,
        null,
      ],
    ];

    // 一覧整形
    raw.list.forEach((listItem, i) => {
      this.pushAlternateTemplateRow(templateRows, i, templateConfig.list.row.odd, templateConfig.list.row.even);
      values.push([
        listItem.番号,
        listItem.役員名,
        '役員借入金',
        this.convertByMoneyUnit(listItem.前期末残高),
        this.convertByMoneyUnit(listItem.元本返済額月),
        AppFormatUtility.formatYm(listItem.返済開始時期),
        FormatUtility.percent(FormatUtility.toFixed(listItem.金利, 3)),
        this.convertByMoneyUnit(listItem.直近月残高),
        listItem.備考,
      ]);
    });

    // 合計整形
    templateRows.push(templateConfig.list.total);
    values.push([
      ...this.templateCellValues(3),
      this.convertByMoneyUnit(raw.total.前期末残高),
      this.convertByMoneyUnit(raw.total.元本返済額月),
      ...this.templateCellValues(2),
      this.convertByMoneyUnit(raw.total.直近月残高),
      null,
    ]);

    return {
      templateRows,
      values,
    };
  }
}
